import graphql from "babel-plugin-relay/macro";
import { useAtomValue, useSetAtom } from "jotai";
import { useEffect, useMemo } from "react";
import { useFragment } from "react-relay";

import { selectedRoundIdAtom } from "@/screens/JoinBooking/ConfirmPanel/atoms/stores";
import {
  Pressable,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { JoiningUserRow } from "../JoiningUserRow/JoiningUserRow";
import { SelectableGolfRound } from "./SelectableGolfRound";
import { SelectRound_Booking$key } from "./__generated__/SelectRound_Booking.graphql";

export const SelectRound = ({
  bookingRef,
  firstName,
  lastName,
}: {
  bookingRef: SelectRound_Booking$key | null;
  firstName?: string;
  lastName?: string;
}) => {
  const booking = useFragment(SelectRoundFragmentString, bookingRef);
  /**
   * get a list of rounds with no player on them.
   * Then filter out any duplicates so we offer only
   * unique options to the user.
   */
  const joinableRounds = useMemo(
    () =>
      booking?.rounds?.edges
        ?.filter((round) => !round?.node?.player?.id)
        ?.flatMap((round) => (round?.node ? [round.node] : []))
        ?.filter(
          (round, index, self) =>
            index ===
            self.findIndex(
              (r) =>
                r.courseLayout?.holeCount === round.courseLayout?.holeCount &&
                r.transportationType?.id === round.transportationType?.id &&
                r.roundRate?.rate?.id === round.roundRate?.rate?.id
            )
        ) ?? [],
    [booking]
  );

  const selectedRoundId = useAtomValue(selectedRoundIdAtom);
  const setSelectedRoundId = useSetAtom(selectedRoundIdAtom);

  useEffect(() => {
    if (joinableRounds.length > 0 && selectedRoundId === null) {
      setSelectedRoundId(joinableRounds[0].id);
    }
  }, [joinableRounds, selectedRoundId, setSelectedRoundId]);

  return (
    <VStack gap={4}>
      <JoiningUserRow
        firstName={firstName ?? null}
        lastName={lastName ?? null}
      />
      <VStack>
        {joinableRounds.map((round) => (
          <Pressable
            key={round.id}
            paddingVertical={3}
            onPress={() => setSelectedRoundId(round.id)}
          >
            <SelectableGolfRound
              roundRef={round}
              isSelected={round.id === selectedRoundId}
            />
          </Pressable>
        ))}
      </VStack>
    </VStack>
  );
};

const SelectRoundFragmentString = graphql`
  fragment SelectRound_Booking on Booking {
    rounds {
      edges {
        node {
          id
          courseLayout {
            holeCount
          }
          transportationType {
            id
          }
          roundRate {
            rate {
              id
            }
          }
          ...SelectableGolfRound_Round
          player {
            id
          }
        }
      }
    }
  }
`;
